import { useState } from "react";
import { useParams } from "react-router";
import { standBezeichnung } from "@dj/domain/revier";
import { Kachel } from "../design/Kachel";
import { RueckgaengigLeiste, useRueckgaengig } from "../design/Rueckgaengig";
import { Symbol } from "../design/Symbol";
import type { Zeile } from "../plattform/lesemodelle";
import { anrufen, navigieren } from "../plattform/position";
import { personAus, positionAus, useMeinEintrag, useRevierSammlung, type Rahmen } from "./daten";

/**
 * Abholliste (oberflaeche.md §6, Domänenmodell §13; F-110, F-111, F-116): je Anstellgruppe, die die Person lesen darf
 * (Ansteller der Gruppe, Leitung), eine `region` „Anstellgruppe <AG>“ und je Stand ein `article` „Stand <Nr>“ mit dem
 * Schützen, seinem Anstellstatus und „angestellt“, „abgeholt“, „Anrufen“, „Navigation“. „angestellt“ und „abgeholt“
 * melden mit Rückgängig (§5 „Abholen“, ADR-0023); die Rückgängig-Leiste steht in der Abholliste selbst, damit der
 * Ansteller in der Liste bleibt. Ein Statuswechsel gegen die Pfeilrichtung ist nicht anwählbar (Domänenmodell §6); was
 * dieses Gerät gerade gemeldet hat, ist bis zur Antwort nicht noch einmal anwählbar.
 */
type Abholmeldung = "angestellt" | "abgeholt";

export function Abholliste({ rahmen }: { rahmen: Rahmen }) {
  const { id = "" } = useParams();
  const { revier } = useMeinEintrag(id);
  const liste = useRevierSammlung(id, revier, "abholliste");
  const rg = useRueckgaengig();
  const [gemeldet, setGemeldet] = useState<Record<string, Abholmeldung>>({});
  const gruppen = [...new Set(liste.map((z) => String(z["Anstellgruppe"])))].sort((a, b) => a.localeCompare(b, "de"));

  const melden = (z: Zeile, meldung: Abholmeldung) => {
    const schuetze = personAus(z["Schütze"]);
    if (!schuetze) return;
    setGemeldet({ ...gemeldet, [schuetze.Kennung]: meldung });
    rg.vormerken({
      befehl: meldung === "angestellt" ? "SchuetzeAngestelltMelden" : "SchuetzeAbgeholtMelden", begriff: meldung,
      symbol: meldung === "angestellt" ? "stand" : "abgeholt", angaben: [["stand", `Stand ${String(z["Stand"])} · ${schuetze.Name}`]],
      titel: () => `Stand ${String(z["Stand"])}`, daten: { "Drückjagd": id, "Schütze": schuetze.Kennung },
    });
  };

  const zuruecknehmen = () => {
    const kennung = rg.offen?.daten["Schütze"] as string | undefined;
    rg.zuruecknehmen();
    if (kennung) {
      const { [kennung]: _weg, ...rest } = gemeldet;
      setGemeldet(rest);
    }
  };

  const zahlen = (zeilen: Zeile[]) => {
    const zahl = (status: string) => zeilen.filter((z) => z["Anstellstatus"] === status).length;
    return <Zahlen paare={[[zeilen.length, "Stände"], [zahl("am Stand"), "am Stand"], [zahl("abgeholt"), "abgeholt"]]} />;
  };
  const nachStand = (zeilen: Zeile[]) => [...zeilen].sort((a, b) => Number(a["_reihenfolge"] ?? 0) - Number(b["_reihenfolge"] ?? 0));
  const eintrag = (z: Zeile) => {
    const s = personAus(z["Schütze"]);
    return <Stand key={z._id} zeile={z} gemeldet={s ? gemeldet[s.Kennung] ?? null : null} onMelden={(m) => melden(z, m)} />;
  };

  // Liste nach der großen Hauptaktion (design/README §4.4 K71, K76; Referenz `#s-abholliste`): der erste Eintrag über dem
  // Abdruck der Hauptaktion „Abholliste“, die Zusammenfassung ohne Bedienelement füllt den Abdruck, dann die übrigen
  // Einträge; bei großer Schrift steht die Zusammenfassung als Kopf über der Liste. Eine Anstellgruppe steht im Titel,
  // mehrere als Bereiche.
  const eineGruppe = gruppen.length === 1;
  return rahmen({
    drueckjagdId: id, name: "Abholliste", abdruck: true, unter: eineGruppe ? `Anstellgruppe ${gruppen[0]}` : undefined,
    listenkopf: <p className="liste__zus liste__zahlen platte" role="status">{zahlen(liste)}</p>,
    unten: rg.offen ? <RueckgaengigLeiste offen={rg.offen} onRueckgaengig={zuruecknehmen} /> : undefined,
    children: (
      <>
        {gruppen.map((g, i) => {
          const zeilen = nachStand(liste.filter((z) => z["Anstellgruppe"] === g));
          const kopf = `abholliste-${zeilen[0]?._id ?? g}`;
          const [erster, ...uebrige] = zeilen;
          return (
            <section key={g} className="liste-bereich" role="region" {...(eineGruppe ? { "aria-label": "Abholliste" } : { "aria-labelledby": kopf })}>
              {eineGruppe ? null : <h2 className="kopf platte gruppenkopf" id={kopf}><Symbol name="ansteller" />Anstellgruppe {g}</h2>}
              {erster ? eintrag(erster) : null}
              {i === 0 ? <p className="liste__abdruck liste__zahlen platte" role="status">{zahlen(liste)}</p> : null}
              {uebrige.map(eintrag)}
            </section>
          );
        })}
        {gruppen.length === 0 ? <p className="leer platte"><Symbol name="ansteller" /><span>Keine Anstellgruppe</span></p> : null}
      </>
    ),
  });
}

/** Stand der Liste als Zahlen (Referenz `liste__zahlen`): je Zahl und Wort ein Paar. */
function Zahlen({ paare }: { paare: Array<[number, string]> }) {
  return <>{paare.map(([z, w]) => <span key={w} className="zahl"><b>{z}</b> <span>{w}</span></span>)}</>;
}

function Stand({ zeile, gemeldet, onMelden }: { zeile: Zeile; gemeldet: Abholmeldung | null; onMelden: (m: Abholmeldung) => void }) {
  const schuetze = personAus(zeile["Schütze"]);
  const status = (zeile["Anstellstatus"] as string | null | undefined) ?? null;
  const telefon = (zeile["Telefon"] as string | null | undefined) ?? null;
  const ziel = positionAus(zeile["Navigation zu"]);
  // Bis die Meldung dieses Geräts im Lesemodell steht, gilt sie als gemeldet (kein zweites „abgeholt“).
  const angestelltMoeglich = !!schuetze && status === "offen" && !gemeldet;
  const abgeholtMoeglich = !!schuetze && status === "am Stand" && gemeldet !== "abgeholt";
  return (
    <article className="eintrag eintrag--knoepfe eintrag--zeile platte" aria-label={`Stand ${String(zeile["Stand"])}`}>
      <h3 className="eintrag__t">{standBezeichnung(String(zeile["Stand"]), zeile["Standname"] as string | null | undefined)}</h3>
      <span className="eintrag__meta">{schuetze ? [schuetze.Name, status].filter(Boolean).join(" · ") : "unbesetzt"}</span>
      <div className="raster raster--knoepfe">
        <Kachel symbol="stand" begriff="angestellt" art="kompakt" isDisabled={!angestelltMoeglich} onPress={() => onMelden("angestellt")} />
        <Kachel symbol="abgeholt" begriff="abgeholt" art="kompakt" isDisabled={!abgeholtMoeglich} onPress={() => onMelden("abgeholt")} />
        <Kachel symbol="telefon" begriff="Anrufen" art="kompakt" isDisabled={!telefon} onPress={() => anrufen(telefon)} />
        <Kachel symbol="navigation" begriff="Navigation" art="kompakt" isDisabled={!ziel} onPress={() => navigieren(ziel)} />
      </div>
    </article>
  );
}
